import React, { useState } from 'react'
import styled from 'styled-components'
import { ProfileData, ModuleGroupData } from './types'
import { ModuleSection } from './ModuleSection'

const CardContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  cursor: pointer;
  user-select: none;
  background-color: #ffffff;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f8fafc;
  }
`

const LeftHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const ArrowIcon = styled.span`
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ProfileName = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: 0.3px;
`

const RightHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const UserCountText = styled.span`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`

const SubmenuPill = styled.span`
  background-color: #e0f2fe;
  color: #0284c7;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 16px;
`

const SaveButton = styled.button`
  background-color: #0284c7;
  color: #ffffff;
  border: none;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #0369a1;
  }

  &:disabled {
    background-color: #94a3b8;
    cursor: not-allowed;
  }
`

const CardBody = styled.div`
  padding: 8px 20px 20px 20px;
  border-top: 1px solid #f1f5f9;
  background-color: #fafafa;
`

interface ProfileCardProps {
  profile: ProfileData
  onProfileChange: (updatedProfile: ProfileData) => void
  onSaveProfile?: (profile: ProfileData) => void
  isSaving?: boolean
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  onProfileChange,
  onSaveProfile,
  isSaving = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const totalSubmenus = profile.modules.reduce((acc, mod) => acc + mod.submenus.length, 0)
  const activeSubmenus = profile.modules.reduce((acc, mod) => {
    return acc + mod.submenus.filter((s) => s.permissions.ver).length
  }, 0)

  const handleModuleChange = (updatedModule: ModuleGroupData) => {
    const updatedModules = profile.modules.map((m) => (m.id === updatedModule.id ? updatedModule : m))
    onProfileChange({ ...profile, modules: updatedModules })
  }

  return (
    <CardContainer>
      <CardHeader onClick={() => setIsExpanded((prev) => !prev)}>
        <LeftHeader>
          <ArrowIcon>{isExpanded ? '▼' : '▶'}</ArrowIcon>
          <ProfileName>{profile.name}</ProfileName>
        </LeftHeader>

        <RightHeader>
          <UserCountText>
            {profile.userCount} {profile.userCount === 1 ? 'usuário' : 'usuários'}
          </UserCountText>
          <SubmenuPill>
            {activeSubmenus} de {totalSubmenus} submenus
          </SubmenuPill>
          {isExpanded && onSaveProfile && (
            <SaveButton
              disabled={isSaving}
              onClick={(e) => {
                e.stopPropagation()
                onSaveProfile(profile)
              }}
            >
              {isSaving ? 'Salvando...' : 'Salvar Alterações'}
            </SaveButton>
          )}
        </RightHeader>
      </CardHeader>

      {isExpanded && (
        <CardBody>
          {profile.modules.map((moduleGroup) => (
            <ModuleSection
              key={moduleGroup.id}
              moduleGroup={moduleGroup}
              onModuleChange={handleModuleChange}
            />
          ))}
        </CardBody>
      )}
    </CardContainer>
  )
}
